/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';
import type { Skill_CareerRole as CareerRole } from '__generated__/graphql-types-do-not-use';

import type { TrackingData } from 'js/lib/retracked';

import { Button, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import { isIntentionRoutingEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { getProductDifficultyLevelsTranslated } from 'bundles/browse/utils';
import type { PathwaySpecializationOnRdp } from 'bundles/unified-career-academy/hooks/useCareerRoleData';
import GradientBadge from 'bundles/unified-common/components/recommended-pathway/GradientBadge';
import PathwayCourseProgression from 'bundles/unified-common/components/recommended-pathway/PathwayCourseProgression';
import RecommendedPathwayBody from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayBody';
import RecommendedPathwayComponent from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayComponent';
import RecommendedPathwayEnrollButtonNonConfigurable from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayEnrollButtonNonConfigurable';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import getPathwayUpsellCaption from 'bundles/unified-common/utils/getPathwayUpsellCaption';

import _t from 'i18n!nls/career-academy';

type Props = PathwaySpecializationOnRdp & {
  showTopRecommendedBadge?: boolean;
  showCoach?: boolean;
  trackingData: TrackingData;
  alternativePathways?: PathwaySpecializationOnRdp[];
  role?: { name?: string; slug?: string };
};

const styles = {
  coachButton: css`
    &.pathways-recommendation-coach {
      span.cds-button-label {
        color: var(--cds-color-interactive-primary);
        ${typography2.actionSecondary};
      }

      &[aria-disabled='true'] {
        .cds-button-label {
          color: var(--cds-color-neutral-disabled);
        }
      }
    }
  `,
  buttonOutline: css`
    ${breakpoints.down('sm')} {
      box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary);
      border-radius: var(--cds-spacing-50);
    }
  `,
};

const PathwayCard = ({
  name,
  partners,
  urlPath,
  skillTagsForXdp,
  rating,
  proficiencyLevel,
  monthsToCompleteOverride,
  courses,
  id,
  slug,
  targetDegrees,
  showCoach,
  showTopRecommendedBadge,
  trackingData,
  alternativePathways,
  role,
}: Props) => {
  const { setCoachOpen, setContextConfig } = useCoachState();
  const trackV3 = useTracker();
  if (!name) {
    return null;
  }

  const metadata: string[] = [];
  const translatedDifficultyLevel = proficiencyLevel
    ? getProductDifficultyLevelsTranslated(proficiencyLevel)
    : undefined;
  if (translatedDifficultyLevel) {
    metadata.push(translatedDifficultyLevel);
  }

  if (monthsToCompleteOverride) {
    metadata.push(_t('#{months} months', { months: monthsToCompleteOverride }));
  }

  if (targetDegrees?.items && targetDegrees.items.length > 0) {
    metadata.push(getPathwayUpsellCaption(targetDegrees.items[0]?.stackingType));
  }

  const isIntentRoutingEnabled = isIntentionRoutingEnabled();

  const getCoachActionButtonConfig = (
    primaryId?: string,
    alternatives?: PathwaySpecializationOnRdp[],
    roleContext?: { name?: CareerRole['name']; slug?: CareerRole['slug'] }
  ): AiCoachRespondToUserMessageInput => {
    return {
      courseraContext: {
        specializationId: primaryId,
        additionalProducts: alternatives?.map(({ id: alternativeProductId, __typename }) => ({
          productId: alternativeProductId,
          productType: __typename === 'Specialization_Specialization' ? 'SPECIALIZATION' : 'COURSE',
        })),
      },
      config: {
        coachModeId: isIntentRoutingEnabled ? 'PATHWAYS_RDP' : 'PATHWAYS_MVP_TEST_MODE',
        coachActionId: 'HELP_ME_DECIDE_ACTION',
      },
      message: _t('Why is this recommended?'),
      additionalContext: [
        {
          contextKey: 'additionalContext.roleData',
          contextValue: roleContext ? `role-name: ${roleContext?.name}, role-slug: ${roleContext?.slug}.` : '',
        },
      ],
    };
  };

  const coachActionButtonConfig = getCoachActionButtonConfig(id, alternativePathways, role);

  return (
    <RecommendedPathwayComponent
      badge={showTopRecommendedBadge ? <GradientBadge label={_t('Top recommendation')} /> : undefined}
      logo={partners[0]?.squareLogo}
      name={name}
      url={urlPath}
      courseProgression={<PathwayCourseProgression courseData={courses} totalCourses={courses.length} />}
      body={
        <RecommendedPathwayBody
          skills={skillTagsForXdp?.map((skill) => skill.skillName).filter(Boolean)}
          averageFiveStarRating={rating?.averageFiveStarRating}
          ratingCount={rating?.ratingCount}
          metadata={metadata}
        />
      }
      trackingData={{
        index: trackingData.index,
        productCard: trackingData.productCard,
        id,
        slug,
        carousel: trackingData.carousel,
      }}
      buttons={[
        <RecommendedPathwayEnrollButtonNonConfigurable
          key="enroll"
          buttonProps={{ size: 'small' }}
          s12nId={id}
          partnerIds={partners?.map((partner) => partner.id)}
          courses={courses}
          proficiencyLevel={proficiencyLevel}
          trackingData={{
            button: {
              name: 'enroll',
            },
            product: {
              id,
              slug,
            },
            productCard: {
              index: trackingData.productCard.index,
              recommender: trackingData.productCard.recommender,
            },
          }}
        />,
        <Button
          component="a"
          key="details"
          size="small"
          variant="ghost"
          css={styles.buttonOutline}
          href={urlPath}
          onClick={() =>
            trackV3('click_button', {
              button: {
                name: 'program_info',
                linkName: 'pathway_module_xdp_link',
                linkURL: urlPath,
              },
              product: {
                id,
                slug,
              },
              productCard: trackingData.productCard,
            })
          }
        >
          {_t('View details')}
        </Button>,
        ...(showCoach
          ? [
              <ExternalCoachAction
                css={[styles.coachButton, styles.buttonOutline]}
                size="small"
                key="ai"
                variant="ghost"
                newSessionOnClick={true}
                onClick={() => {
                  const { message, ...coachChatContext } = coachActionButtonConfig;
                  setContextConfig({
                    ...coachChatContext,
                    config: {
                      coachModeId: isIntentRoutingEnabled ? 'PATHWAYS_RDP' : 'PATHWAYS_MVP_RDP_MODE',
                      coachActionId: isIntentRoutingEnabled ? 'ROUTER' : 'TYPED_MESSAGE',
                    },
                  });
                  setCoachOpen(true);
                }}
                label={_t('Why is this recommended?')}
                {...coachActionButtonConfig}
                className="pathways-recommendation-coach"
              />,
            ]
          : []),
      ]}
    />
  );
};

export default PathwayCard;
